import { useRef } from 'react'
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import type { PluginListenerHandle } from '@capacitor/core'
import { isPlatformWeb } from '../../../utilities'

const KEYBOARD_HEIGHT_VAR = '--qa-keyboard-height'
const KEYBOARD_OPEN_CLASS = 'qa-keyboard-open'

/** Keeps QA flag pages in keyboard overlay mode **/
export const useKeyboardOverlay = (): void => {
  const previousModeRef = useRef<KeyboardResize | undefined>(undefined)
  const listenersRef = useRef<PluginListenerHandle[]>([])

  useIonViewWillEnter(() => {
    if (isPlatformWeb()) return

    Keyboard.getResizeMode()
      .then(({ mode }) => {
        previousModeRef.current = mode
        return Keyboard.setResizeMode({ mode: KeyboardResize.None })
      })
      .catch(() => undefined)

    const root = document.documentElement
    const setKeyboardHeight = (height: number) => {
      root.style.setProperty(KEYBOARD_HEIGHT_VAR, `${height}px`)
      root.classList.toggle(KEYBOARD_OPEN_CLASS, height > 0)
    }

    Keyboard.addListener('keyboardWillShow', (info) => setKeyboardHeight(info.keyboardHeight))
      .then((handle) => listenersRef.current.push(handle))
      .catch(() => undefined)
    Keyboard.addListener('keyboardWillHide', () => setKeyboardHeight(0))
      .then((handle) => listenersRef.current.push(handle))
      .catch(() => undefined)
  })

  useIonViewWillLeave(() => {
    listenersRef.current.forEach((handle) => handle.remove())
    listenersRef.current = []

    const root = document.documentElement
    root.style.removeProperty(KEYBOARD_HEIGHT_VAR)
    root.classList.remove(KEYBOARD_OPEN_CLASS)

    const previousMode = previousModeRef.current
    if (isPlatformWeb() || !previousMode) return
    Keyboard.setResizeMode({ mode: previousMode }).catch(() => undefined)
  })
}
